import React, { useEffect, useRef, useState } from "react";
import TemplatePreviewButton from "@/components/documents/TemplatePreviewButton";
import { docusealService } from "@/services/docuseal";
import type { CustomUploadSent } from "../types";

const LOAD_FAILED = "Could not load the document.";

/**
 * What a Custom upload flow sent: the signed copy once stored, else the uploaded PDF.
 * `compact` shortens the visible label (the accessible name stays whole) for the rail.
 */
export default function SentDocumentButton({
  upload,
  compact = false,
  document = false,
}: {
  upload: CustomUploadSent;
  compact?: boolean;
  /** Sent as a Document: the stored copy is the PDF emailed to the merchant, unsigned. */
  document?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  // The raw (axios) message means nothing to staff: any failure reads the same.
  const onError = (message: string | null) => setFailed(message != null);
  const rootRef = useRef<HTMLDivElement>(null);
  // The note stays until the next try (which clears it), Escape, or a click elsewhere.
  useEffect(() => {
    if (!failed) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFailed(false);
    };
    const onPointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setFailed(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointer);
    };
  }, [failed]);
  const { external_id: externalId, document_id: documentId } = upload;
  let button: React.ReactNode = null;
  if (upload.has_signed_document && externalId) {
    button = (
      <TemplatePreviewButton
        label={
          document
            ? compact
              ? "View"
              : "View document"
            : compact
              ? "View signed"
              : "View signed document"
        }
        ariaLabel={compact ? (document ? "View document" : "View signed document") : undefined}
        title={document ? "The PDF emailed to the merchant" : "The signed copy"}
        load={() => docusealService.downloadSignedDocument(externalId)}
        onError={onError}
      />
    );
  } else if (documentId != null) {
    button = (
      <TemplatePreviewButton
        documentId={documentId}
        label={compact ? "View" : "View document"}
        ariaLabel={compact ? "View document" : undefined}
        // A Document has no filled-in copy to contrast with.
        title={
          document ? "The uploaded PDF" : "The uploaded PDF, not the merchant's filled-in copy"
        }
        onError={onError}
      />
    );
  }
  if (!button) return null;
  return (
    // The note floats under the button, so a failure never shifts the card or modal.
    <div ref={rootRef} className="pointer-events-auto relative">
      {button}
      {failed ? (
        <p
          role="alert"
          className="absolute right-0 top-full z-10 mt-1 w-max max-w-[14rem] rounded-md border bg-popover px-2 py-1 text-xs text-destructive shadow-md"
        >
          {LOAD_FAILED}
        </p>
      ) : null}
    </div>
  );
}
